import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { ClarityModule } from '@clr/angular';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BroadcastsService } from '../../../features/programplan/services/broadcasts.service';

export interface UploadCompleteEvent {
  s3Key: string;
  title: string;
  fileName: string;
  fileSize: number;
}

@Component({
  selector: 'app-upload-dropzone',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule],
  templateUrl: './upload-dropzone.component.html',
  styleUrl: './upload-dropzone.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class UploadDropzoneComponent {
  private broadcastsService = inject(BroadcastsService);
  private http = inject(HttpClient);

  // Configuration inputs
  @Input() s3Prefix = 'ARCHIV';
  @Input() allowedExtensions = ['.mp4', '.wav'];
  @Input() maxFileSizeBytes = 2 * 1024 * 1024 * 1024; // 2GB default
  @Input() dropzoneText = 'Datei hier ablegen';
  @Input() dropzoneHint = 'MP4 oder WAV (max. 2 GB)';
  @Input() modalTitle = 'Datei hochladen';
  @Input() titleLabel = 'Titel *';
  @Input() titlePlaceholder = 'Titel der Sendung eingeben';
  /** RBAC: sichtbar, aber gesperrt — blockiert Klick- UND Drag&Drop-Pfad. */
  @Input() disabled = false;

  // Events
  @Output() uploadComplete = new EventEmitter<UploadCompleteEvent>();
  @Output() uploadError = new EventEmitter<string>();

  // State
  isDragging = false;
  isUploading = false;
  uploadProgress = 0;
  showUploadModal = false;
  uploadTitle = '';
  selectedFile: File | null = null;
  errorMessage = '';

  // Drag & Drop handlers
  onDragOver(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    if (this.disabled) return;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
    }
    // Reset input so same file can be selected again
    input.value = '';
  }

  private handleFileSelection(file: File): void {
    this.errorMessage = '';

    // Validate file extension
    const extension = this.getFileExtension(file.name).toLowerCase();
    if (!this.allowedExtensions.includes(extension)) {
      this.errorMessage = `Ungültiger Dateityp. Erlaubt sind nur: ${this.allowedExtensions.join(', ')}`;
      this.uploadError.emit(this.errorMessage);
      return;
    }

    // Validate file size
    if (file.size > this.maxFileSizeBytes) {
      const maxSizeGB = (this.maxFileSizeBytes / (1024 * 1024 * 1024)).toFixed(0);
      this.errorMessage = `Datei ist zu groß. Maximale Größe: ${maxSizeGB} GB`;
      this.uploadError.emit(this.errorMessage);
      return;
    }

    this.selectedFile = file;
    // Pre-fill title from filename (without extension)
    this.uploadTitle = file.name.replace(/\.(mp4|wav)$/i, '');
    this.showUploadModal = true;
  }

  private getFileExtension(filename: string): string {
    const match = filename.match(/\.[^.]+$/);
    return match ? match[0] : '';
  }

  private sanitizeFilename(title: string): string {
    return title
      .replace(/[^a-zA-Z0-9äöüÄÖÜß_\-]/g, '_')  // Replace special chars with underscore
      .replace(/_+/g, '_')                       // Collapse multiple underscores
      .replace(/^_|_$/g, '');                    // Trim leading/trailing underscores
  }

  private generateS3Key(title: string, extension: string): string {
    const timestamp = new Date().toISOString();
    const sanitizedTitle = this.sanitizeFilename(title);
    // Include .broadcast before extension: e.g., Goran_Test5.broadcast.mp4
    return `${this.s3Prefix}/${timestamp}/${sanitizedTitle}.broadcast${extension}`;
  }

  uploadFile(): void {
    if (!this.selectedFile || !this.uploadTitle.trim()) {
      this.errorMessage = 'Bitte geben Sie einen Titel ein.';
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.errorMessage = '';

    const extension = this.getFileExtension(this.selectedFile.name);
    const s3Key = this.generateS3Key(this.uploadTitle.trim(), extension);

    this.broadcastsService.getPresignedUrlForPut(s3Key).subscribe({
      next: (result) => {
        const presignedUrl = result?.data?.uploadURL;
        if (!presignedUrl) {
          this.errorMessage = 'Fehler beim Abrufen der Upload-URL.';
          this.isUploading = false;
          this.uploadError.emit(this.errorMessage);
          return;
        }

        const headers = new HttpHeaders({
          'Content-Type': this.selectedFile!.type || 'application/octet-stream'
        });

        const req = new HttpRequest('PUT', presignedUrl, this.selectedFile, {
          reportProgress: true,
          headers
        });

        this.http.request(req).subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              this.uploadProgress = Math.round(100 * event.loaded / event.total);
            } else if (event.type === HttpEventType.Response) {
              // Upload complete
              console.log('Upload erfolgreich:', s3Key);
              this.uploadComplete.emit({
                s3Key,
                title: this.uploadTitle.trim(),
                fileName: this.selectedFile!.name,
                fileSize: this.selectedFile!.size
              });
              this.resetUploadState();
            }
          },
          error: (err) => {
            console.error('Upload-Fehler:', err);
            this.errorMessage = 'Fehler beim Hochladen der Datei.';
            this.isUploading = false;
            this.uploadError.emit(this.errorMessage);
          }
        });
      },
      error: (err) => {
        console.error('Fehler beim Abrufen der Upload-URL:', err);
        this.errorMessage = 'Fehler beim Abrufen der Upload-URL.';
        this.isUploading = false;
        this.uploadError.emit(this.errorMessage);
      }
    });
  }

  cancelUpload(): void {
    this.resetUploadState();
  }

  private resetUploadState(): void {
    this.showUploadModal = false;
    this.isUploading = false;
    this.uploadProgress = 0;
    this.uploadTitle = '';
    this.selectedFile = null;
    this.errorMessage = '';
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  }
}
